// Bundled creator catalog used when Supabase isn't configured (demo mode).
// IDs are 1-indexed and must stay in lockstep with supabase/seed.sql, so a
// `/creators/$id` deep link resolves to the same person in demo mode and
// against the seeded database. src/data/demoSeedParity.test.ts guards the
// alignment.
import type { Creator } from '../types';

export const CREATORS = [
  {
    id: 1,
    name: 'Jordan Reeves',
    handle: '@jordanreeves',
    niche: 'Fitness',
    tags: ['UGC', 'Cinematic'],
    emoji: '🏋️',
    bg: '#1a1208',
    saved: false,
    platforms: ['TikTok', 'Instagram'],
    badges: ['verified', 'topPerformer'],
    reach: '248K',
    engagement: '6.4%',
    completed: 14,
    rate: '$1,800',
    rateNum: 1800,
    bio: 'Fitness creator specializing in high-energy UGC and cinematic brand videos. 5 years creating content for supplement and apparel brands.',
    pitch:
      "I don't just film workouts — I build momentum. Every brand I work with gets content that hits hard in the first three seconds, because that's where attention lives. My audience trusts me because I only put my name on products I actually use at 6am before the gym. If you want a partner who treats your campaign like their own launch, obsesses over the hook, and delivers ahead of deadline every single time, let's talk.",
    portfolio: ['🏋️', '💪', '🥗', '🏃', '⚡', '🎯'],
    brands: ['Apex Nutrition', 'Nike Training', 'GNC'],
    rating: 4.9,
    reviews: [
      {
        brand: 'Apex Nutrition',
        rating: 5,
        text: 'Jordan delivered exceptional content ahead of schedule. The hook in the first 3 seconds drove a 4.2x ROAS.',
        date: 'May 2025',
      },
      {
        brand: 'Nike Training',
        rating: 5,
        text: 'Incredibly professional. Took the brief and elevated it. Will absolutely book again.',
        date: 'Mar 2025',
      },
      {
        brand: 'GNC',
        rating: 4,
        text: 'Great energy and strong brand alignment. Minor revision needed on the CTA but resolved same day.',
        date: 'Jan 2025',
      },
    ],
  },
  {
    id: 2,
    name: 'Maya Chen',
    handle: '@mayachen.creates',
    niche: 'Fashion',
    tags: ['Aesthetic', 'Trending', 'Unboxing'],
    emoji: '👗',
    bg: '#10101a',
    saved: false,
    platforms: ['TikTok', 'Instagram'],
    badges: ['verified', 'topPerformer'],
    reach: '512K',
    engagement: '4.9%',
    completed: 31,
    rate: '$2,400',
    rateNum: 2400,
    bio: 'Fashion and lifestyle creator known for highly aesthetic, trend-forward content. Specialty in product styling and unboxing videos.',
    pitch:
      "Aesthetic is a language, and I'm fluent in it. When your product lands on my feed, it doesn't look like an ad — it looks like the thing everyone suddenly wants. I obsess over lighting, styling, and the tiny details that make a scroll stop. Brands come to me when they want their launch to feel like a moment, not a post. If you care about how your product is *felt*, not just seen, I'm your creator.",
    portfolio: ['👗', '👠', '💄', '🪞', '✨', '🛍️'],
    brands: ['ZARA', 'Revolve', 'Nordstrom'],
    rating: 4.7,
    reviews: [
      {
        brand: 'Revolve',
        rating: 5,
        text: 'Maya\u2019s aesthetic is unmatched. Our engagement on this campaign was the highest we\u2019ve seen all year.',
        date: 'Jun 2025',
      },
      {
        brand: 'ZARA',
        rating: 5,
        text: 'On-brand, on-time, and beautiful execution. Exactly what we needed for the launch.',
        date: 'Apr 2025',
      },
      {
        brand: 'Nordstrom',
        rating: 4,
        text: 'Strong creative instincts. The final product looked stunning.',
        date: 'Feb 2025',
      },
    ],
  },
  {
    id: 3,
    name: 'Caleb St. James',
    handle: '@calebstjames',
    niche: 'Faith-Based',
    tags: ['Faith-Based', 'Storytelling'],
    emoji: '✨',
    bg: '#0e1410',
    saved: true,
    platforms: ['Instagram', 'YouTube'],
    badges: ['verified', 'fastDelivery'],
    reach: '189K',
    engagement: '8.2%',
    completed: 9,
    rate: '$950',
    rateNum: 950,
    bio: 'Faith-based storyteller creating values-driven content for brands that align with a faith-forward audience.',
    pitch:
      "I believe the best content comes from a place of conviction. I partner with brands whose mission I genuinely believe in, and my community can feel the difference — that's why they show up, listen, and act. I'm not here to shout; I'm here to tell honest stories that connect values to real life. If your brand stands for something and you want it shared with heart, I'd be honored to carry that message.",
    portfolio: ['✨', '📖', '🌅', '🙏', '🌿', '💛'],
    brands: ['Hallow', 'DaySpring', 'Proverbs 31'],
    rating: 5.0,
    reviews: [
      {
        brand: 'Hallow',
        rating: 5,
        text: 'Caleb understood our mission deeply. The content felt authentic and resonated powerfully with our audience.',
        date: 'May 2025',
      },
      {
        brand: 'DaySpring',
        rating: 5,
        text: 'Rare find. Caleb brings genuine heart to every piece of content he creates.',
        date: 'Mar 2025',
      },
    ],
  },
  {
    id: 4,
    name: 'Sofia Voss',
    handle: '@sofiavoss',
    niche: 'Tech',
    tags: ['Tech', 'Comedy', 'Tutorial'],
    emoji: '💻',
    bg: '#161208',
    saved: false,
    platforms: ['YouTube', 'TikTok'],
    badges: ['verified', 'topPerformer'],
    reach: '334K',
    engagement: '5.7%',
    completed: 22,
    rate: '$2,100',
    rateNum: 2100,
    bio: 'Tech reviewer and comedy creator blending honest product reviews with humor. Audience skews 18\u201334 tech-forward consumers.',
    pitch:
      "Honest reviews with a punchline \u2014 that's my whole thing. My audience knows I'll tell them when a product is mid, which is exactly why they buy when I say something is great. I make tech feel human and funny instead of spec-sheet boring. Brands that trust me to keep my voice get campaigns that don't feel like campaigns. If you've got a product you actually believe in, I'll make people laugh and click.",
    portfolio: ['💻', '📱', '🎮', '🤖', '⚙️', '🔋'],
    brands: ['Anker', 'Ridge', 'Notion'],
    rating: 4.8,
    reviews: [
      {
        brand: 'Notion',
        rating: 5,
        text: 'Sofia\u2019s audience is incredibly engaged. The video drove 3,200 sign-ups in the first 48 hours.',
        date: 'Jun 2025',
      },
      {
        brand: 'Anker',
        rating: 5,
        text: 'Funny, smart, and effective. Our best-performing influencer campaign this quarter.',
        date: 'Apr 2025',
      },
      {
        brand: 'Ridge',
        rating: 4,
        text: 'Great content. Sofia was easy to work with and delivered on brief.',
        date: 'Feb 2025',
      },
    ],
  },
  {
    id: 5,
    name: 'Priya Nair',
    handle: '@priyanair.co',
    niche: 'Wellness',
    tags: ['Aesthetic', 'UGC'],
    emoji: '🌿',
    bg: '#0c1210',
    saved: false,
    platforms: ['Instagram', 'TikTok'],
    badges: ['verified', 'fastDelivery'],
    reach: '203K',
    engagement: '7.1%',
    completed: 17,
    rate: '$1,400',
    rateNum: 1400,
    bio: 'Wellness and clean living creator focused on aesthetic UGC for health, beauty, and lifestyle brands.',
    pitch:
      "Wellness isn't a trend for me — it's how I actually live, and my content reflects that calm, intentional energy. I create soft, aesthetic UGC that feels like a deep breath, and my community leans in because it's real. I only work with brands that genuinely support well-being, so every partnership feels aligned. If you want content that's beautiful, honest, and delivered fast, I'd love to bring your product into my daily ritual.",
    portfolio: ['🌿', '🧘', '🍵', '🌸', '💚', '🫧'],
    brands: ['Ritual', 'Liquid IV', 'Alo Yoga'],
    rating: 4.9,
    reviews: [
      {
        brand: 'Ritual',
        rating: 5,
        text: 'Priya\u2019s content perfectly captured the essence of our brand. High quality and very professional.',
        date: 'May 2025',
      },
      {
        brand: 'Alo Yoga',
        rating: 5,
        text: 'Beautiful creative direction. Our community loved this campaign.',
        date: 'Mar 2025',
      },
      {
        brand: 'Liquid IV',
        rating: 4,
        text: 'Solid delivery and great aesthetic. Would work with Priya again.',
        date: 'Jan 2025',
      },
    ],
  },
  {
    id: 6,
    name: 'Marcus Obi',
    handle: '@marcobi',
    niche: 'Fitness',
    tags: ['Cinematic', 'Raw'],
    emoji: '🎯',
    bg: '#180e08',
    saved: false,
    platforms: ['TikTok', 'YouTube'],
    badges: ['verified', 'topPerformer'],
    reach: '178K',
    engagement: '9.3%',
    completed: 11,
    rate: '$1,600',
    rateNum: 1600,
    bio: 'Raw, cinematic fitness content creator. Known for authentic, documentary-style brand integration that converts.',
    pitch:
      "No filters, no fake energy — just raw, cinematic work that feels earned. I shoot fitness content like a documentary because that's what makes people believe it. My audience follows me for the grind, and they trust what I put in front of them. When I integrate a brand, it looks like part of the story, not an interruption. If you want authenticity that actually converts, I'll show up and put in the work.",
    portfolio: ['🎯', '🏋️', '🥊', '🔥', '💯', '⚡'],
    brands: ['Gymshark', 'GHOST', 'Under Armour'],
    rating: 4.8,
    reviews: [
      {
        brand: 'Gymshark',
        rating: 5,
        text: 'Marcus brings raw authenticity that our audience connects with immediately. Outstanding results.',
        date: 'Jun 2025',
      },
      {
        brand: 'GHOST',
        rating: 5,
        text: 'Cinematic quality that made our product look incredible. High ROI campaign.',
        date: 'Apr 2025',
      },
      {
        brand: 'Under Armour',
        rating: 4,
        text: 'Great energy and strong execution. Minor timing adjustments needed but final product was excellent.',
        date: 'Feb 2025',
      },
    ],
  },
  {
    id: 7,
    name: 'Leila Karimi',
    handle: '@leilakarimi',
    niche: 'Faith-Based',
    tags: ['Faith-Based', 'Lifestyle'],
    emoji: '🌙',
    bg: '#101018',
    saved: false,
    platforms: ['Instagram'],
    badges: ['verified', 'topPerformer'],
    reach: '142K',
    engagement: '10.1%',
    completed: 7,
    rate: '$800',
    rateNum: 800,
    bio: 'Faith and lifestyle creator building an engaged, values-driven community. Specialty in home, family, and intentional living.',
    pitch:
      "My community is small but mighty — and fiercely loyal — because everything I share comes from my actual home and family life. I create warm, intentional content around the moments that matter, and brands that fit naturally into that world see incredible engagement. I say no to a lot so my yeses mean something. If your product belongs in a life built on faith and intention, I'd love to welcome it in authentically.",
    portfolio: ['🌙', '🕯️', '📿', '🌺', '☕', '🏡'],
    brands: ['Magnolia', 'Anthropologie', 'Grove Co.'],
    rating: 5.0,
    reviews: [
      {
        brand: 'Magnolia',
        rating: 5,
        text: 'Leila\u2019s content is warm, genuine, and beautifully crafted. Perfect fit for our brand voice.',
        date: 'May 2025',
      },
      {
        brand: 'Grove Co.',
        rating: 5,
        text: 'Highest engagement rate we\u2019ve seen from any creator partnership. Will absolutely book again.',
        date: 'Mar 2025',
      },
    ],
  },
  {
    id: 8,
    name: 'Dev Sharma',
    handle: '@devsharma.dev',
    niche: 'SaaS',
    tags: ['Tech', 'Tutorial', 'Educational'],
    emoji: '⚡',
    bg: '#141008',
    saved: false,
    platforms: ['YouTube'],
    badges: ['verified', 'topPerformer'],
    reach: '291K',
    engagement: '6.8%',
    completed: 19,
    rate: '$1,900',
    rateNum: 1900,
    bio: 'Developer and tech educator creating tutorial-style content for SaaS, productivity, and developer tools.',
    pitch:
      "I teach, and teaching builds trust. When I walk my audience through a tool, they don't just watch — they open a new tab and sign up, because I've earned the right to recommend. I go deep on the technical details other creators skip, and that credibility is your conversion engine. If you've built something genuinely useful for developers, I'll explain exactly why it matters and let the product do the talking.",
    portfolio: ['⚡', '🖥️', '📊', '🔧', '🚀', '🧑‍💻'],
    brands: ['Figma', 'Linear', 'Vercel'],
    rating: 4.7,
    reviews: [
      {
        brand: 'Figma',
        rating: 5,
        text: 'Dev\u2019s tutorial format is perfect for our product. Clear, credible, and drives real conversions.',
        date: 'Jun 2025',
      },
      {
        brand: 'Linear',
        rating: 5,
        text: 'Exceptional technical depth and audience trust. Our best-performing sponsored video ever.',
        date: 'Apr 2025',
      },
      {
        brand: 'Vercel',
        rating: 4,
        text: 'Great creative and strong delivery. Dev is highly professional throughout the process.',
        date: 'Feb 2025',
      },
    ],
  },
  {
    id: 9,
    name: 'Aaliyah Brooks',
    handle: '@aaliyahbeauty',
    niche: 'Skincare',
    tags: ['Unboxing', 'Testimonial', 'Aesthetic'],
    emoji: '✨',
    bg: '#12080e',
    saved: false,
    platforms: ['TikTok', 'Instagram'],
    badges: ['verified', 'fastDelivery'],
    reach: '167K',
    engagement: '8.8%',
    completed: 12,
    rate: '$1,200',
    rateNum: 1200,
    bio: 'Skincare and beauty creator known for honest testimonials and aesthetic unboxing content. Trusted by clean beauty brands for authentic first-impression reviews.',
    pitch:
      "My audience treats my recommendations like advice from a friend — because that's exactly how I deliver them. I test everything on my own skin before I ever film, so when I say a product works, people believe me and buy. My unboxings are aesthetic, my testimonials are honest, and that combination is why my conversion rates stay high. If your product can hold up to a real first impression, I'll make it a must-have.",
    portfolio: ['✨', '🧴', '💆', '🌸', '💅', '🪷'],
    brands: ['Fenty Beauty', 'CeraVe', 'Tatcha'],
    rating: 4.9,
    reviews: [
      {
        brand: 'Fenty Beauty',
        rating: 5,
        text: 'Aaliyah\u2019s testimonial style is incredibly persuasive. Our conversion rate from this campaign was exceptional.',
        date: 'Jun 2025',
      },
      {
        brand: 'CeraVe',
        rating: 5,
        text: 'Authentic, relatable, and on-brand. She made our product feel like a must-have.',
        date: 'Apr 2025',
      },
      {
        brand: 'Tatcha',
        rating: 4,
        text: 'Beautiful unboxing content. Delivered ahead of schedule with zero revisions needed.',
        date: 'Feb 2025',
      },
    ],
  },
  {
    id: 10,
    name: 'Tyler Nash',
    handle: '@tylernash',
    niche: 'Streetwear',
    tags: ['Cinematic', 'Lifestyle', 'Trending'],
    emoji: '👟',
    bg: '#0e0e12',
    saved: false,
    platforms: ['TikTok', 'Instagram'],
    badges: ['verified'],
    reach: '223K',
    engagement: '7.4%',
    completed: 15,
    rate: '$1,700',
    rateNum: 1700,
    bio: 'Streetwear and sneaker culture creator producing cinematic lifestyle content. Known for high-energy hauls, drops, and brand collabs that resonate with Gen Z.',
    pitch:
      "I live and breathe street culture, so when I put your product on, it instantly reads as authentic to Gen Z — the audience everyone's chasing. My hauls and drops feel like events because I shoot them cinematic and hype them like the real thing. Brands trust me to make product look aspirational without losing the edge. If you want your drop to actually move and get shared, I'll bring the energy and the eyes.",
    portfolio: ['👟', '🧢', '🪡', '🕶️', '🎒', '🔥'],
    brands: ['New Balance', 'Supreme', 'KITH'],
    rating: 4.8,
    reviews: [
      {
        brand: 'New Balance',
        rating: 5,
        text: 'Tyler\u2019s content captured exactly the energy we wanted. His audience is the perfect demographic.',
        date: 'May 2025',
      },
      {
        brand: 'Supreme',
        rating: 5,
        text: 'Cinematic quality, authentic voice. This was our most-shared campaign of the season.',
        date: 'Mar 2025',
      },
      {
        brand: 'KITH',
        rating: 4,
        text: 'Strong delivery and great brand fit. Tyler knows how to make product look aspirational.',
        date: 'Jan 2025',
      },
    ],
  },
  {
    id: 11,
    name: 'Emma Foster',
    handle: '@emmafoster.home',
    niche: 'Home Decor',
    tags: ['Aesthetic', 'UGC', 'Storytelling'],
    emoji: '🏡',
    bg: '#10100c',
    saved: false,
    platforms: ['Instagram', 'YouTube'],
    badges: ['verified'],
    reach: '198K',
    engagement: '6.9%',
    completed: 13,
    rate: '$1,100',
    rateNum: 1100,
    bio: 'Home decor and interior styling creator specializing in aesthetic UGC and before-and-after transformations. Perfect for home goods, furniture, and lifestyle brands.',
    pitch:
      "I make products look like they belong in a magazine — because styling is my superpower. My before-and-after transformations stop the scroll and make people picture your product in their own home. My audience comes to me for taste, and they trust the pieces I bring into my space. If you want home goods that feel aspirational yet attainable, I'll style them so beautifully your customers can't imagine their room without them.",
    portfolio: ['🏡', '🪴', '🛋️', '🕯️', '🖼️', '☕'],
    brands: ['IKEA', 'West Elm', 'Article'],
    rating: 4.7,
    reviews: [
      {
        brand: 'West Elm',
        rating: 5,
        text: 'Emma\u2019s styling eye is extraordinary. She made our products look like they belong in a magazine.',
        date: 'Jun 2025',
      },
      {
        brand: 'IKEA',
        rating: 4,
        text: 'Great creative direction and reliable delivery. Her audience is highly engaged with home content.',
        date: 'Apr 2025',
      },
      {
        brand: 'Article',
        rating: 5,
        text: 'Emma brought our furniture to life in a way our own team couldn\u2019t. Will absolutely collaborate again.',
        date: 'Feb 2025',
      },
    ],
  },
  {
    id: 12,
    name: 'Marcus Chen',
    handle: '@marcuschen.gg',
    niche: 'Gaming',
    tags: ['Tutorial', 'Comedy', 'Trending'],
    emoji: '🎮',
    bg: '#0c0c14',
    saved: false,
    platforms: ['YouTube', 'TikTok'],
    badges: ['verified', 'topPerformer'],
    reach: '445K',
    engagement: '5.2%',
    completed: 24,
    rate: '$2,200',
    rateNum: 2200,
    bio: 'Gaming and entertainment creator mixing tutorials with comedy. Massive reach in the 16\u201328 demographic for gaming peripherals, energy drinks, and tech accessories.',
    pitch:
      "I've got massive reach with the hardest audience to win over \u2014 16-to-28 gamers who can smell a fake sponsorship instantly. My trick is simple: I only rep gear I actually game with, and I make the integration funny enough that nobody skips it. That authenticity is why my recommendations convert. If you want to reach gamers without getting clowned in the comments, I'll make your product part of the fun.",
    portfolio: ['🎮', '🕹️', '🖥️', '🎯', '⚡', '🏆'],
    brands: ['Razer', 'G Fuel', 'HyperX'],
    rating: 4.6,
    reviews: [
      {
        brand: 'Razer',
        rating: 5,
        text: 'Marcus\u2019 integration felt completely natural. His audience trusted the recommendation immediately.',
        date: 'Jun 2025',
      },
      {
        brand: 'G Fuel',
        rating: 4,
        text: 'Great engagement and authentic delivery. His comedy style made the product memorable.',
        date: 'Apr 2025',
      },
      {
        brand: 'HyperX',
        rating: 5,
        text: 'Top-tier gaming creator. The tutorial format drove significant traffic to our product page.',
        date: 'Feb 2025',
      },
    ],
  },
  {
    id: 13,
    name: 'Zara Williams',
    handle: '@zarawilliamseats',
    niche: 'Food Content',
    tags: ['Aesthetic', 'Unboxing', 'Storytelling'],
    emoji: '🍽️',
    bg: '#120e08',
    saved: false,
    platforms: ['TikTok', 'Instagram', 'YouTube'],
    badges: ['verified', 'topPerformer'],
    reach: '312K',
    engagement: '7.6%',
    completed: 20,
    rate: '$1,500',
    rateNum: 1500,
    bio: 'Food and beverage content creator known for visually stunning recipe content and aesthetic product integrations. Specialty in DTC food brands and kitchen products.',
    pitch:
      "Food is emotional, and I shoot it to make people hungry and curious at the same time. My recipe content is visually stunning, but it also tells a story that makes your product the hero of the meal. My audience saves, shares, and actually cooks what I make — which means real intent for your brand. If you've got a food or kitchen product worth savoring, I'll make it look irresistible and give people a reason to try it tonight.",
    portfolio: ['🍽️', '🥘', '🧁', '🍷', '🫙', '🌶️'],
    brands: ['HelloFresh', 'Graza', 'Le Creuset'],
    rating: 4.9,
    reviews: [
      {
        brand: 'HelloFresh',
        rating: 5,
        text: 'Zara\u2019s food styling is incredible. The campaign drove our highest sign-up week of the year.',
        date: 'May 2025',
      },
      {
        brand: 'Graza',
        rating: 5,
        text: 'She made our olive oil look like fine art. Authentic, beautiful, and highly effective.',
        date: 'Mar 2025',
      },
      {
        brand: 'Le Creuset',
        rating: 5,
        text: 'Zara understands product storytelling at a deep level. Flawless execution every time.',
        date: 'Jan 2025',
      },
    ],
  },
  {
    id: 14,
    name: 'James Okafor',
    handle: '@jamesokafor',
    niche: 'Entrepreneurship',
    tags: ['Storytelling', 'Educational', 'Testimonial'],
    emoji: '💼',
    bg: '#0e1008',
    saved: false,
    platforms: ['YouTube', 'Instagram'],
    badges: ['verified', 'topPerformer'],
    reach: '256K',
    engagement: '8.4%',
    completed: 18,
    rate: '$2,000',
    rateNum: 2000,
    bio: 'Entrepreneur and business creator building content around startup culture, productivity, and financial freedom. Trusted voice for B2B SaaS, fintech, and business tools.',
    pitch:
      "I've built businesses, so when I talk about a tool, founders listen — I've been in their seat. My content turns dry business topics into stories people actually finish, and my audience is exactly the high-intent decision-makers B2B brands want. I only recommend what I'd put in my own stack. If you've built something that genuinely helps entrepreneurs work smarter, I'll make the case in a way that drives real, qualified signups.",
    portfolio: ['💼', '📈', '🧠', '💡', '🚀', '🎯'],
    brands: ['Shopify', 'QuickBooks', 'Calendly'],
    rating: 4.8,
    reviews: [
      {
        brand: 'Shopify',
        rating: 5,
        text: 'James\u2019 storytelling approach drove genuine intent. His audience is exactly our target customer.',
        date: 'Jun 2025',
      },
      {
        brand: 'QuickBooks',
        rating: 5,
        text: 'Credible, relatable, and persuasive. James made our product feel essential for any founder.',
        date: 'Apr 2025',
      },
      {
        brand: 'Calendly',
        rating: 4,
        text: 'Solid campaign with strong message alignment. Educational format worked perfectly for our product.',
        date: 'Feb 2025',
      },
    ],
  },
  {
    id: 15,
    name: 'Nina Rodriguez',
    handle: '@ninarodriguez.re',
    niche: 'Real Estate',
    tags: ['Testimonial', 'Educational', 'Storytelling'],
    emoji: '🏘️',
    bg: '#100c08',
    saved: false,
    platforms: ['YouTube', 'Instagram'],
    badges: ['verified', 'topPerformer', 'fastDelivery'],
    reach: '134K',
    engagement: '9.7%',
    completed: 8,
    rate: '$1,300',
    rateNum: 1300,
    bio: 'Real estate and personal finance creator helping first-time buyers and investors navigate the market. High-trust audience ideal for mortgage, proptech, and investment platforms.',
    pitch:
      "I take intimidating money topics and make them feel doable — that's why first-time buyers and new investors trust me with the biggest decisions of their lives. That trust is rare and it converts, because when I recommend a platform, my audience believes it'll actually help them. I keep it honest and educational, never salesy. If you're in proptech, mortgage, or investing and want a credible voice that drives sign-ups, I'm your partner.",
    portfolio: ['🏘️', '🔑', '📋', '🏙️', '💰', '📊'],
    brands: ['Zillow', 'Better.com', 'Roofstock'],
    rating: 4.9,
    reviews: [
      {
        brand: 'Zillow',
        rating: 5,
        text: 'Nina\u2019s educational approach builds incredible trust with her audience. Our sign-ups from this campaign were outstanding.',
        date: 'May 2025',
      },
      {
        brand: 'Better.com',
        rating: 5,
        text: 'She breaks down complex topics in a way that actually converts. Highly recommend for fintech campaigns.',
        date: 'Mar 2025',
      },
      {
        brand: 'Roofstock',
        rating: 4,
        text: 'Authentic and credible. Nina\u2019s audience is highly engaged with real estate content.',
        date: 'Jan 2025',
      },
    ],
  },
  {
    id: 16,
    name: 'Sam Park',
    handle: '@sampark.brand',
    niche: 'Personal Branding',
    tags: ['Storytelling', 'Cinematic', 'Educational'],
    emoji: '🎬',
    bg: '#08080e',
    saved: false,
    platforms: ['TikTok', 'YouTube'],
    badges: ['verified', 'topPerformer'],
    reach: '188K',
    engagement: '8.1%',
    completed: 10,
    rate: '$1,650',
    rateNum: 1650,
    bio: 'Personal branding and content strategy creator helping entrepreneurs build their digital presence. Cinematic storytelling style with a focus on authenticity and positioning.',
    pitch:
      "I help people find their voice, so I know exactly how to make your brand's voice land. My storytelling is cinematic and my positioning is sharp — I don't just show a product, I frame why it matters. My audience of builders and creators trusts my taste in tools because I use them to make the very content they admire. If you want a partnership that feels premium and intentional, I'll make your brand part of my story.",
    portfolio: ['🎬', '🎯', '📸', '✍️', '🌟', '💫'],
    brands: ['Adobe Express', 'Canva', 'Squarespace'],
    rating: 4.8,
    reviews: [
      {
        brand: 'Adobe Express',
        rating: 5,
        text: 'Sam\u2019s storytelling is exactly what our brand needed. High production value and genuine enthusiasm for the product.',
        date: 'Jun 2025',
      },
      {
        brand: 'Canva',
        rating: 5,
        text: 'Cinematic, polished, and authentic. Sam made Canva feel like a must-have creative tool.',
        date: 'Apr 2025',
      },
      {
        brand: 'Squarespace',
        rating: 4,
        text: 'Great creative direction and strong brand alignment. Sam delivers a premium feel every time.',
        date: 'Feb 2025',
      },
    ],
  },
] satisfies Creator[];
